/**
 * Centralized 401 handler.
 *
 * Called from axios response interceptors (the unified apiClient AND the
 * default Axios used by utils/api.js helpers) whenever the backend rejects
 * a request with 401 Unauthorized — typically because the JWT expired or
 * the IdleTimeoutJWTAuthentication cache key expired mid-session.
 *
 * Previously each call site handled this independently (or not at all),
 * so 401s from utils/api.js helpers just hit a console.error with no
 * logout and no redirect — the user saw a generic error and the page
 * sat there with stale auth state.
 *
 * Also fixes a navigation bug: the SPA uses HashRouter, so
 * `window.location.href = "/login"` left the user on an unmatched route.
 * Use the hash form so the router picks up `/login` correctly.
 */
import { dispatch } from "store";
import { LOGOUT_AUTH } from "store/reducers/auth";

let isHandling = false;

export function handle401() {
  // Guard against concurrent 401s from parallel requests all trying to
  // redirect at once.
  if (isHandling) return;
  isHandling = true;

  try {
    localStorage.removeItem("Token");
    localStorage.removeItem("access_list_data");
    localStorage.removeItem("user_details");
  } catch {
    // localStorage can throw in private-mode browsers; ignore.
  }

  try {
    dispatch(LOGOUT_AUTH());
  } catch {
    // Redux store may not be ready in some early-boot paths; ignore.
  }

  // HashRouter: keep the leading `#/` so the router matches /login.
  // window.location.href = "/login" would land on a path the router
  // can't see, producing a blank screen on the deploy.
  if (typeof window !== "undefined") {
    try {
      window.location.hash = "#/login";
    } catch {
      window.location.href = "/#/login";
    }
  }
}

export function isAuthError(error) {
  const status = error?.response?.status;
  return status === 401;
}
